import esriConfig from '@arcgis/core/config';

import { Injectable } from '@angular/core';

import { FsDataDogRumService } from '@lib/data-dog-rum';

/**
 * Service for monitoring ESRI/ArcGIS tile loading latency.
 *
 * Monitors tile loading performance for ESRI maps via ESRI's built-in request interceptors.
 * Logs slow requests, timeouts, and errors to DataDog for performance tracking.
 *
 * This approach uses ESRI's native request interceptors which provide accurate timing
 * that matches what you see in the browser network tab.
 */
@Injectable({
  providedIn: 'root',
})
export class EsriTileMonitorService {
  private static _isInitialized = false;

  private readonly TEN_SECONDS_MS = 10_000;
  private readonly _requestTimings = new Map<string, number>();

  private readonly ESRI_TILE_URL_PATTERNS = [
    /.+\/arcgis\/rest\/.*/i,
    /\/WMTS\//i,
    /\/tile\/\d+\/\d+\/\d+/i,
  ];

  constructor(private readonly _datadogRumService: FsDataDogRumService) {}

  public initialize(): void {
    if (EsriTileMonitorService._isInitialized) {
      return;
    }

    this._setupEsriRequestInterceptor();
    EsriTileMonitorService._isInitialized = true;
  }

  private _logSlowRequest(
    url: string,
    durationMs: number,
    status = 0,
    timedOut = false,
    isError = false,
  ): void {
    const isSlow = durationMs >= this.TEN_SECONDS_MS;
    const isBlocked = status === 0 && !timedOut && isError;
    const shouldLog = isSlow || timedOut || isBlocked;

    if (shouldLog) {
      // Log to DataDog
      this._datadogRumService.addAction('map_tiles_timeout', {
        timeoutMs: durationMs,
        failed: isError,
        url,
        provider: 'esri',
      });
    }
  }

  private _setupEsriRequestInterceptor(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const service = this;

    esriConfig.request.interceptors.push({
      urls: this.ESRI_TILE_URL_PATTERNS,
      before: (params: any) => {
        const startTime = performance.now();
        service._requestTimings.set(params.url, startTime);
      },
      after: (response: any) => {
        const endTime = performance.now();
        const startTime = service._requestTimings.get(response.url) ?? endTime;
        const durationMs = Math.round(endTime - startTime);
        const status = response?.response?.status ?? 200;

        // Clean up the timing entry
        service._requestTimings.delete(response.url);

        service._logSlowRequest(response.url, durationMs, status, false);
        return response;
      },
      error: (err: any) => {
        const endTime = performance.now();
        const startTime = service._requestTimings.get(err?.url || '') ?? endTime;
        const durationMs = Math.round(endTime - startTime);
        const status = err?.response?.status ?? 0;

        // Clean up the timing entry
        service._requestTimings.delete(err?.url || '');

        service._logSlowRequest(err?.url || '', durationMs, status, false, true);
        throw err;
      },
    });
  }
}
